import { useEffect, useState } from 'react';
import { competitionRoundsService } from '../../services/competition-rounds';
import { useGymAdmin } from '../context/gym-admin-context';
import type { CompetitionRound } from '../../types/competition-round';

/**
 * How many times the list has been said to have changed, and who wants telling.
 *
 * The menu lists the rounds, and the page that makes, renames and unmakes them
 * is somewhere else entirely — the sidebar is mounted for the whole visit, so
 * nothing in the tree is in a position to hand it the news. Hence a notice of
 * its own rather than a prop: one line at the end of a save, and the menu never
 * shows a round under its old name or offers one that is gone.
 */
let revision = 0;
const listeners = new Set<() => void>();

/** Says the competition's rounds have changed: whoever lists them refetches. */
export function refreshCompetitionRounds(): void {
  revision += 1;
  listeners.forEach((listener) => listener());
}

/**
 * The selected competition's rounds, for the menu that lists them.
 *
 * `enabled` is the caller's own test — the sidebar asks only where the rounds
 * entry is drawn at all — so that a gym's or a circuit's panel never calls a
 * competition-only endpoint.
 *
 * A failure leaves the menu without its rounds and says nothing about it: the
 * sidebar is the wrong place to report a load error, and the page the entry
 * leads to reports it where somebody can act on it.
 */
export function useCompetitionRounds(enabled: boolean): CompetitionRound[] {
  const { selectedGymSlug } = useGymAdmin();
  const [rounds, setRounds] = useState<CompetitionRound[]>([]);
  const [seen, setSeen] = useState(revision);

  useEffect(() => {
    const listener = () => setSeen(revision);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !selectedGymSlug) {
      setRounds([]);
      return;
    }

    let current = true;
    competitionRoundsService
      .getRounds(selectedGymSlug)
      .then((loaded) => {
        if (current) setRounds(loaded);
      })
      .catch(() => {
        if (current) setRounds([]);
      });

    return () => {
      current = false;
    };
  }, [enabled, selectedGymSlug, seen]);

  return rounds;
}
